import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useState } from 'preact/hooks';
import { useForm, Controller } from 'react-hook-form';
import { Settings as SettingsIcon, FileCheck, Users, Shield, Loader2 } from 'lucide-preact';
import { UserPicker, Select, NumberInput, Checkbox, FormActions } from '@/components';
import { BRAND_CLASSES } from '@/constants/theme';
import { useToast } from '@/hooks/useToast';
import { setFormValidationErrors } from '@/utils/errorHandling';
import { useLookups } from '@/hooks/useLookups';
import { InheritSettingsToggle } from '@/components/common/InheritSettingsToggle';
import { workflowSettingsApi } from '@/api/workflowSettings';
import { loadEffectiveSettings } from '@/state/workflowSettings';
export function AssetWorkflowSettings({ level, workspaceId, clientCompanyId, campaignId, webId }) {
    const { showError, showSuccess, showErrorUnless422 } = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [existingSettings, setExistingSettings] = useState(null);
    const { getAssetStatuses } = useLookups();
    const form = useForm({
        mode: 'onChange',
        defaultValues: {
            inheritFromParent: level !== 'workspace',
            defaultReviewerUsers: [],
            internalTeamUsers: [],
            defaultStatusOnUpload: '',
            requireSignOffConfirmation: false,
            allowCommentsAfterSignOff: true,
            internalOnlyReopen: false,
            allowImages: true,
            allowDocuments: true,
            allowVideos: true,
            allowArchives: true,
            maxFileSizeMB: 500,
            allowedExtensions: ''
        }
    });
    const { register, handleSubmit, formState, control, reset } = form;
    const { errors, isDirty } = formState;
    const inheritFromParent = form.watch('inheritFromParent');
    const showForm = level === 'workspace' || !inheritFromParent;
    // Fetch settings based on level
    const fetchSettings = async () => {
        switch (level) {
            case 'workspace':
                return workflowSettingsApi.getWorkspaceSettings(clientCompanyId, workspaceId);
            case 'campaign':
                return workflowSettingsApi.getCampaignSettings(clientCompanyId, workspaceId, campaignId);
            case 'web':
                return workflowSettingsApi.getWebSettings(clientCompanyId, workspaceId, webId);
        }
    };
    // Save settings based on level
    const saveSettings = async (dto) => {
        switch (level) {
            case 'workspace':
                return workflowSettingsApi.updateWorkspaceSettings(clientCompanyId, workspaceId, dto);
            case 'campaign':
                return workflowSettingsApi.updateCampaignSettings(clientCompanyId, workspaceId, campaignId, dto);
            case 'web':
                return workflowSettingsApi.updateWebSettings(clientCompanyId, workspaceId, webId, dto);
        }
    };
    // Helper to populate form from settings response
    const populateForm = (settings) => {
        const maxFileSizeMB = Math.round(settings.FileRestrictions.MaxFileSizeBytes / 1048576);
        reset({
            inheritFromParent: settings.InheritFromParent,
            defaultReviewerUsers: settings.DefaultReviewerUsers || [],
            internalTeamUsers: settings.InternalTeamUsers || [],
            defaultStatusOnUpload: settings.DefaultStatusOnUpload || '',
            requireSignOffConfirmation: settings.RequireSignOffConfirmation || false,
            allowCommentsAfterSignOff: settings.AllowCommentsAfterSignOff !== false,
            internalOnlyReopen: settings.InternalOnlyReopen || false,
            allowImages: settings.FileRestrictions.AllowImages !== false,
            allowDocuments: settings.FileRestrictions.AllowDocuments !== false,
            allowVideos: settings.FileRestrictions.AllowVideos !== false,
            allowArchives: settings.FileRestrictions.AllowArchives !== false,
            maxFileSizeMB,
            allowedExtensions: settings.FileRestrictions.AllowedExtensions?.join(', ') || ''
        });
    };
    // Load existing settings
    useEffect(() => {
        let active = true;
        const loadData = async () => {
            setLoading(true);
            const settingsResponse = await fetchSettings();
            if (!active)
                return;
            if (settingsResponse.data) {
                const settings = settingsResponse.data;
                setExistingSettings(settings);
                populateForm(settings);
            }
            else if (settingsResponse.error && settingsResponse.status !== 404) {
                showError('Failed to load settings', settingsResponse.error);
            }
            setLoading(false);
        };
        loadData();
        return () => {
            active = false;
        };
    }, [level, clientCompanyId, workspaceId, campaignId, webId, showError, reset]);
    const onSubmit = handleSubmit(async (data) => {
        setSaving(true);
        const maxFileSizeBytes = Math.round(data.maxFileSizeMB * 1048576);
        const allowedExtensions = data.allowedExtensions
            ? data.allowedExtensions.split(',').map(ext => ext.trim()).filter(Boolean)
            : undefined;
        const dto = {
            WorkspaceId: workspaceId,
            CampaignId: campaignId,
            WebId: webId,
            InheritFromParent: level === 'workspace' ? false : data.inheritFromParent,
            DefaultReviewerUserIds: data.defaultReviewerUsers.map(u => u.Id),
            InternalTeamUserIds: data.internalTeamUsers.map(u => u.Id),
            DefaultStatusOnUpload: data.defaultStatusOnUpload,
            RequireSignOffConfirmation: data.requireSignOffConfirmation,
            AllowCommentsAfterSignOff: data.allowCommentsAfterSignOff,
            InternalOnlyReopen: data.internalOnlyReopen,
            FileRestrictions: {
                AllowImages: data.allowImages,
                AllowDocuments: data.allowDocuments,
                AllowVideos: data.allowVideos,
                AllowArchives: data.allowArchives,
                MaxFileSizeBytes: maxFileSizeBytes,
                AllowedExtensions: allowedExtensions
            },
            ETag: existingSettings?.ETag
        };
        const response = await saveSettings(dto);
        if (response.data) {
            setExistingSettings(response.data);
            showSuccess('Settings saved', 'Workflow settings have been updated successfully.');
            reset(data);
            // Refresh the effective settings signal so modals pick up changes
            if (level === 'web' && campaignId && webId) {
                loadEffectiveSettings(clientCompanyId, workspaceId, campaignId, webId);
            }
        }
        else if (response.error) {
            if (response.status === 422) {
                setFormValidationErrors(response, form.setError);
            }
            else {
                showError('Save failed', response.error || 'Unable to save workflow settings');
            }
        }
        setSaving(false);
    });
    // Handle toggling inheritance on/off
    const handleInheritToggle = async (checked) => {
        form.setValue('inheritFromParent', checked, { shouldDirty: true });
        if (checked) {
            // Save immediately with InheritFromParent=true
            setSaving(true);
            const dto = {
                WorkspaceId: workspaceId,
                CampaignId: campaignId,
                WebId: webId,
                InheritFromParent: true,
                DefaultReviewerUserIds: existingSettings?.DefaultReviewerUsers?.map(u => u.Id) || [],
                InternalTeamUserIds: existingSettings?.InternalTeamUsers?.map(u => u.Id) || [],
                DefaultStatusOnUpload: existingSettings?.DefaultStatusOnUpload || '',
                RequireSignOffConfirmation: existingSettings?.RequireSignOffConfirmation || false,
                AllowCommentsAfterSignOff: existingSettings?.AllowCommentsAfterSignOff !== false,
                InternalOnlyReopen: existingSettings?.InternalOnlyReopen || false,
                FileRestrictions: existingSettings?.FileRestrictions || {
                    AllowImages: true,
                    AllowDocuments: true,
                    AllowVideos: true,
                    AllowArchives: true,
                    MaxFileSizeBytes: 524288000,
                },
                ETag: existingSettings?.ETag
            };
            const response = await saveSettings(dto);
            if (response.data) {
                setExistingSettings(response.data);
                showSuccess('Settings saved', 'Now inheriting settings from the parent level.');
                form.reset({ ...form.getValues(), inheritFromParent: true });
                // Refresh the effective settings signal so modals pick up changes
                if (level === 'web' && campaignId && webId) {
                    loadEffectiveSettings(clientCompanyId, workspaceId, campaignId, webId);
                }
            }
            else if (response.error) {
                showErrorUnless422('Save failed', response, 'Unable to save settings');
                form.setValue('inheritFromParent', false);
            }
            setSaving(false);
        }
    };
    const handleCancel = () => {
        if (existingSettings) {
            populateForm(existingSettings);
        }
    };
    const isSaveDisabled = saving || !formState.isValid || !isDirty;
    const saveText = saving ? 'Saving...' : isDirty ? 'Save Changes' : 'No Changes';
    if (loading) {
        return (_jsxs("div", { className: "p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300", children: [_jsx("div", { className: "w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), "Loading workflow settings..."] }));
    }
    const assetStatuses = getAssetStatuses();
    const statusOptions = assetStatuses.map(s => ({ value: s.Description, label: s.Description }));
    return (_jsxs("div", { children: [level !== 'workspace' && (_jsx(InheritSettingsToggle, { level: level, checked: inheritFromParent, onChange: handleInheritToggle })), showForm && (_jsxs("div", { className: "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm", children: [_jsxs("div", { className: "flex items-center gap-3 mb-6", children: [_jsx("span", { className: "relative inline-flex", children: _jsx(SettingsIcon, { className: "w-5 h-5 text-primary-600 dark:text-primary-500" }) }), _jsx("h2", { className: "text-lg font-semibold text-slate-900 dark:text-white tracking-tight", children: "Asset Workflow Settings" }), _jsxs("button", { type: "submit", form: "asset-workflow-settings-form", disabled: isSaveDisabled, className: `ml-auto px-4 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${BRAND_CLASSES.BUTTON.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`, children: [saving && _jsx(Loader2, { className: "w-3.5 h-3.5 animate-spin" }), saveText] })] }), _jsxs("form", { id: "asset-workflow-settings-form", onSubmit: onSubmit, className: "space-y-8", children: [_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700", children: [_jsx(Users, { className: "w-4 h-4 text-slate-500 dark:text-slate-400" }), _jsx("h3", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide", children: "Team Members" })] }), _jsx(Controller, { name: "defaultReviewerUsers", control: control, render: ({ field, fieldState }) => (_jsx(UserPicker, { label: "Default Reviewers", placeholder: "Search for reviewers...", helperText: "Users who will review and approve assets by default", workspaceId: workspaceId, clientCompanyId: clientCompanyId, userType: "reviewer", selectedUsers: field.value || [], onChange: field.onChange, error: fieldState.error?.message, containerClassName: "grid-cols-1" })) }), _jsx(Controller, { name: "internalTeamUsers", control: control, render: ({ field, fieldState }) => (_jsx(UserPicker, { label: "Internal Team", placeholder: "Search for internal team members...", helperText: "Internal team members who can manage the workflow", workspaceId: workspaceId, clientCompanyId: clientCompanyId, userType: "internal", selectedUsers: field.value || [], onChange: field.onChange, error: fieldState.error?.message, containerClassName: "grid-cols-1" })) })] }), _jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700", children: [_jsx(Shield, { className: "w-4 h-4 text-slate-500 dark:text-slate-400" }), _jsx("h3", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide", children: "Approval Settings" })] }), _jsx(Select, { label: "Default Status on Upload", required: true, placeholder: "Select default status", helperText: "Status that assets will receive when first uploaded", options: statusOptions, error: errors.defaultStatusOnUpload?.message, ...register('defaultStatusOnUpload', {
                                            required: 'Default status is required'
                                        }) }), _jsxs("div", { className: "space-y-3", children: [_jsx(Checkbox, { label: "Require Sign-Off Confirmation", description: "Users must explicitly confirm when signing off on assets", ...register('requireSignOffConfirmation') }), _jsx(Checkbox, { label: "Allow Comments After Sign-Off", description: "Enable commenting on assets even after they've been approved", ...register('allowCommentsAfterSignOff') }), _jsx(Checkbox, { label: "Internal Only Reopen", description: "Only internal team members can reopen approved assets", ...register('internalOnlyReopen') })] })] }), _jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-700", children: [_jsx(FileCheck, { className: "w-4 h-4 text-slate-500 dark:text-slate-400" }), _jsx("h3", { className: "text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide", children: "File Restrictions" })] }), _jsxs("div", { children: [_jsx("label", { className: `block text-sm font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-1`, children: "Allowed File Types" }), _jsx("p", { className: `text-xs ${BRAND_CLASSES.INPUT.HELPER} mb-4`, children: "Unchecked file types will not be accepted for upload" }), _jsx("div", { className: "grid grid-cols-2 gap-3", children: ([
                                                    { field: 'allowImages', label: 'Images', extensions: 'jpg, jpeg, png, gif, svg, webp, bmp, tiff' },
                                                    { field: 'allowDocuments', label: 'Documents', extensions: 'pdf, doc, docx, ppt, pptx, xls, xlsx, txt, rtf' },
                                                    { field: 'allowVideos', label: 'Videos', extensions: 'mp4, mov, avi, wmv, mkv, webm' },
                                                    { field: 'allowArchives', label: 'Archives', extensions: 'zip, rar, tar, gz, 7z' },
                                                ]).map(({ field, label, extensions }) => {
                                                    const checked = form.watch(field);
                                                    return (_jsx(Checkbox, { containerClassName: `relative p-4 rounded-xl border cursor-pointer transition-all ${checked
                                                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/15 shadow-sm'
                                                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'}`, label: label, description: extensions, ...register(field) }, field));
                                                }) })] }), _jsx(NumberInput, { label: "Maximum File Size (MB)", required: true, placeholder: "500", helperText: "Maximum size for uploaded files in megabytes", error: errors.maxFileSizeMB?.message, ...register('maxFileSizeMB', {
                                            required: 'Maximum file size is required',
                                            min: { value: 1, message: 'Must be at least 1 MB' },
                                            max: { value: 5000, message: 'Cannot exceed 5000 MB (5 GB)' }
                                        }) }), _jsxs("div", { children: [_jsxs("label", { htmlFor: "allowedExtensions", className: `block text-sm font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-2`, children: ["Allowed Extensions", _jsx("span", { className: "ml-1 text-xs font-normal text-slate-500 dark:text-slate-400", children: "(Optional)" })] }), _jsx("input", { type: "text", id: "allowedExtensions", placeholder: ".pdf, .docx, .jpg, .png", className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`, ...register('allowedExtensions') }), _jsx("p", { className: `mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: "Comma-separated list of specific file extensions to allow (leave empty for all)" })] })] }), _jsx(FormActions, { isSubmitting: saving, isSaveDisabled: isSaveDisabled || !isDirty, onCancel: handleCancel, saveText: saveText })] })] }))] }));
}
